import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FeatureFlags from 'constants/featureFlags';
import { logInfo } from 'services/Logger';
import { NotificationType } from 'contexts/websocket/provider';
import { useFeatureFlag } from './useFeatureFlag';
import useNotification from './useNotification';

const NOTIFICATION_GRACE_PERIOD = 60000;

const useNetworkConnectivity = ({
  isBrowserConnected,
  isSalesdeskSocketConnected,
  isRocketChatSocketConnected,
}: {
  isBrowserConnected: boolean;
  isSalesdeskSocketConnected: boolean;
  isRocketChatSocketConnected: boolean;
}) => {
  const shouldHandleUnstableNetwork = useFeatureFlag(FeatureFlags.HANDLE_UNSTABLE_NETWORK);
  const { t } = useTranslation();
  const { notify } = useNotification();
  const wasRocketChatSocketConnected = useRef(false);
  const wasSalesdeskSocketConnected = useRef(false);
  const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!shouldHandleUnstableNetwork) {
      return undefined;
    }

    if (isSalesdeskSocketConnected) {
      wasSalesdeskSocketConnected.current = true;
    }

    if (isRocketChatSocketConnected) {
      wasRocketChatSocketConnected.current = true;
    }

    const checks = {
      isBrowserDisconnected: !isBrowserConnected,
      isSalesdeskSocketDisconnected: !isSalesdeskSocketConnected && wasSalesdeskSocketConnected.current,
      isRocketChatSocketDisconnected: !isRocketChatSocketConnected && wasRocketChatSocketConnected.current,
    };

    const hasConnectivityIssue = Object.values(checks).some(Boolean);
    if (hasConnectivityIssue && !notificationTimerRef.current) {
      notificationTimerRef.current = setTimeout(() => {
        logInfo('Connectivity issues', checks);
        notify(
          {
            title: t('connectivtyIssuesTitle'),
            description: t('connectivtyIssuesAction'),
            chatId: '',
            type: NotificationType.NEW_MESSAGE,
          },
          () => window.location.reload()
        );
      }, NOTIFICATION_GRACE_PERIOD);
    } else if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
      notificationTimerRef.current = null;
    }

    return () => {
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
        notificationTimerRef.current = null;
      }
    };
  }, [
    isBrowserConnected,
    isRocketChatSocketConnected,
    isSalesdeskSocketConnected,
    notify,
    shouldHandleUnstableNetwork,
    t,
  ]);
};

export default useNetworkConnectivity;
